import { makeStyles } from "@material-ui/core";

// Shared admin styles for consistent look across all dashboard pages
export const useAdminStyles = makeStyles((theme) => ({
  // ── Page header (title + action buttons) ──
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 22,
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  pageHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },
  pageBackBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: "1px solid rgba(76,0,76,0.14)",
    background: "#ffffff",
    color: "#4c004c",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    transition: "all 0.25s ease",
    padding: 0,
    "& svg": { fontSize: 18 },
    "&:hover": {
      background: "rgba(76,0,76,0.06)",
      borderColor: "#4c004c",
      transform: "translateX(-2px)",
    },
  },
  pageTitleBlock: {
    minWidth: 0,
  },
  pageEyebrow: {
    fontSize: "0.6rem",
    fontWeight: 700,
    color: "#9b3b9b",
    textTransform: "uppercase",
    letterSpacing: "1.6px",
    marginBottom: 2,
  },
  pageTitle: {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#1a1a1a",
    letterSpacing: "-0.01em",
    lineHeight: 1.2,
    margin: 0,
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.2rem",
    },
  },
  pageSubtitle: {
    fontSize: "0.78rem",
    color: "#6b7280",
    marginTop: 4,
    lineHeight: 1.5,
  },

  // ── Buttons ──
  primaryBtn: {
    background: "linear-gradient(135deg, #4c004c 0%, #6b1b6b 100%)",
    color: "#ffffff",
    padding: "10px 20px",
    borderRadius: 11,
    fontSize: "0.82rem",
    fontWeight: 600,
    textTransform: "none",
    letterSpacing: "0.2px",
    boxShadow: "0 6px 16px rgba(76,0,76,0.22)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    "&, &:hover, &:focus, &:active, &:visited": {
      color: "#ffffff",
      textDecoration: "none",
    },
    "&:hover": {
      background: "linear-gradient(135deg, #6b1b6b 0%, #9b3b9b 100%)",
      transform: "translateY(-1px)",
      boxShadow: "0 10px 22px rgba(76,0,76,0.32)",
    },
    "&:hover .MuiButton-endIcon": {
      transform: "translateX(3px)",
    },
    "&:disabled": {
      background: "#cccccc",
      color: "#ffffff",
      boxShadow: "none",
    },
  },
  secondaryBtn: {
    background: "#ffffff",
    color: "#4c004c",
    padding: "9px 18px",
    borderRadius: 11,
    fontSize: "0.8rem",
    fontWeight: 600,
    textTransform: "none",
    letterSpacing: "0.2px",
    border: "1px solid rgba(76,0,76,0.18)",
    transition: "all 0.25s ease",
    "&, &:hover, &:focus, &:active, &:visited": {
      color: "#4c004c",
      textDecoration: "none",
    },
    "&:hover": {
      background: "rgba(76,0,76,0.05)",
      borderColor: "#4c004c",
    },
  },
  iconBtn: {
    width: 34,
    height: 34,
    borderRadius: 9,
    border: "1px solid rgba(76,0,76,0.14)",
    background: "#ffffff",
    color: "#4c004c",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    transition: "all 0.25s ease",
    "& svg": { fontSize: 16 },
    "&:hover": {
      background: "rgba(76,0,76,0.06)",
      borderColor: "#4c004c",
    },
  },
  dangerIconBtn: {
    width: 34,
    height: 34,
    borderRadius: 9,
    border: "1px solid rgba(220,38,38,0.18)",
    background: "#ffffff",
    color: "#dc2626",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    transition: "all 0.25s ease",
    "& svg": { fontSize: 16 },
    "&:hover": {
      background: "rgba(220,38,38,0.06)",
      borderColor: "#dc2626",
    },
  },

  // ── Section title ──
  sectionTitle: {
    fontSize: "0.82rem",
    fontWeight: 700,
    color: "#4c004c",
    letterSpacing: "0.2px",
    margin: "8px 0 14px",
    paddingBottom: 8,
    borderBottom: "1px solid rgba(76,0,76,0.08)",
  },

  // ── Form inputs (apply to TextField via className) ──
  textField: {
    "& .MuiOutlinedInput-root, & .MuiFilledInput-root": {
      borderRadius: 10,
      backgroundColor: "rgba(255,255,255,0.9)",
      fontSize: "0.86rem",
      transition: "all 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
      "& fieldset": {
        borderColor: "rgba(76,0,76,0.14)",
        transition: "border-color 0.25s ease, border-width 0.25s ease",
      },
      "&:hover fieldset": {
        borderColor: "rgba(76,0,76,0.32)",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
        boxShadow: "0 0 0 4px rgba(155,59,155,0.08)",
        "& fieldset": {
          borderColor: "#4c004c",
          borderWidth: 1.5,
        },
      },
      "&.Mui-error fieldset": {
        borderColor: "#dc2626",
      },
      "&.Mui-error.Mui-focused": {
        boxShadow: "0 0 0 4px rgba(220,38,38,0.1)",
      },
    },
    "& .MuiOutlinedInput-input, & .MuiFilledInput-input": {
      padding: "12px 12px",
    },
    "& .MuiOutlinedInput-multiline, & .MuiFilledInput-multiline": {
      padding: "12px 12px",
      "& textarea": { padding: 0 },
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.85rem",
      color: "#6b7280",
      "&.Mui-focused": { color: "#4c004c" },
      "&.Mui-error": { color: "#dc2626" },
    },
    "& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after": {
      display: "none",
    },
    "& .MuiSelect-select:focus": {
      backgroundColor: "transparent",
    },
  },

  // ── Image upload box ──
  uploadWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  uploadBox: {
    border: "1.5px dashed rgba(76,0,76,0.22)",
    borderRadius: 12,
    padding: "16px 18px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    cursor: "pointer",
    transition: "all 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
    background: "rgba(76,0,76,0.02)",
    "&:hover": {
      borderColor: "#9b3b9b",
      background: "rgba(155,59,155,0.04)",
    },
  },
  uploadBoxFilled: {
    borderStyle: "solid",
    borderColor: "rgba(76,0,76,0.4)",
    background: "rgba(155,59,155,0.06)",
  },
  uploadIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(155,59,155,0.12)",
    color: "#4c004c",
    flexShrink: 0,
    "& svg": { fontSize: 20 },
  },
  uploadInfo: {
    flex: 1,
    minWidth: 0,
    fontSize: "0.8rem",
    color: "#6b7280",
    "& strong": { color: "#4c004c", fontWeight: 600 },
  },
  uploadHint: {
    fontSize: "0.7rem",
    color: "#9ca3af",
    marginTop: 2,
  },
  uploadPreview: {
    borderRadius: 10,
    border: "1px solid rgba(76,0,76,0.08)",
    boxShadow: "0 4px 12px rgba(76,0,76,0.06)",
    maxWidth: "100%",
    height: "auto",
    display: "block",
    border: "none",
  },

  // ── Status switch label ──
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontSize: "0.84rem",
    color: "#374151",
    fontWeight: 500,
    "& span.on": {
      color: "#10b981",
      fontWeight: 700,
    },
    "& span.off": {
      color: "#9ca3af",
      fontWeight: 600,
    },
  },

  // ── Tables (DataTables override) ──
  tableWrap: {
    "& table.dataTable": {
      width: "100% !important",
      borderCollapse: "separate !important",
      borderSpacing: 0,
      fontSize: "0.82rem",
    },
    "& table.dataTable thead th": {
      background: "#fafafa",
      color: "#4c004c",
      fontSize: "0.7rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      padding: "12px 14px",
      borderBottom: "1px solid rgba(76,0,76,0.08)",
      whiteSpace: "nowrap",
    },
    "& table.dataTable tbody td": {
      padding: "12px 14px",
      borderBottom: "1px solid #f3f4f6",
      fontSize: "0.82rem",
      color: "#374151",
      verticalAlign: "middle",
    },
    "& table.dataTable tbody tr:hover": {
      background: "rgba(155,59,155,0.04)",
    },
    "& table.dataTable img": {
      borderRadius: 8,
      objectFit: "cover",
      border: "1px solid rgba(76,0,76,0.08)",
    },
    "& .dataTables_filter label, & .dataTables_length label": {
      fontSize: "0.76rem",
      color: "#6b7280",
    },
    "& .dataTables_filter input, & .dataTables_length select": {
      padding: "6px 10px",
      borderRadius: 8,
      border: "1px solid rgba(76,0,76,0.14)",
      fontSize: "0.8rem",
      marginLeft: 8,
      outline: "none",
      "&:focus": {
        borderColor: "#4c004c",
      },
    },
    "& .dataTables_info, & .dataTables_paginate": {
      fontSize: "0.76rem",
      color: "#6b7280",
      marginTop: 12,
    },
    "& .dataTables_paginate .paginate_button": {
      padding: "6px 11px !important",
      borderRadius: "8px !important",
      border: "1px solid rgba(76,0,76,0.1) !important",
      background: "#ffffff !important",
      fontSize: "0.78rem !important",
      margin: "0 2px !important",
      color: "#4c004c !important",
      cursor: "pointer",
    },
    "& .dataTables_paginate .paginate_button:hover": {
      background: "rgba(155,59,155,0.08) !important",
      borderColor: "rgba(76,0,76,0.3) !important",
      color: "#4c004c !important",
    },
    "& .dataTables_paginate .paginate_button.current": {
      background:
        "linear-gradient(135deg, #4c004c 0%, #6b1b6b 100%) !important",
      borderColor: "transparent !important",
      color: "#ffffff !important",
    },
    "& .dataTables_paginate .paginate_button.disabled": {
      opacity: 0.4,
      cursor: "not-allowed",
    },
  },

  // ── Action cell helpers ──
  actionCell: {
    display: "flex",
    gap: 6,
    flexWrap: "nowrap",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: "0.66rem",
    fontWeight: 600,
    letterSpacing: "0.2px",
    "& svg": { fontSize: 12 },
  },
  badgeActive: {
    background: "rgba(16,185,129,0.1)",
    color: "#059669",
    border: "1px solid rgba(16,185,129,0.25)",
  },
  badgeInactive: {
    background: "rgba(156,163,175,0.12)",
    color: "#6b7280",
    border: "1px solid rgba(156,163,175,0.32)",
  },

  // ── Form footer ──
  formFooter: {
    display: "flex",
    gap: 12,
    justifyContent: "flex-end",
    marginTop: 26,
    paddingTop: 22,
    borderTop: "1px solid rgba(76,0,76,0.06)",
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column-reverse",
      "& > *": { width: "100%" },
    },
  },
}));
